import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import KnowledgeQuickFilters from '../components/knowledge/KnowledgeQuickFilters.jsx';
import TruthSection from '../components/knowledge/truth/TruthSection.jsx';
import { AccessDeniedState, EmptyState, ErrorState } from '../components/states/index.js';
import { ITEM_KINDS } from '../constants/itemSlideOut.js';
import {
  KNOWLEDGE_SOURCE_TYPES,
  TRUTH_QUICK_FILTERS,
  TRUTH_SEARCH_FIELDS,
  TRUTH_SORT,
  TRUTH_SORT_DIRECTION,
  TRUTH_STATUSES,
} from '../constants/knowledge.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useItemParam } from '../hooks/useItemParam.js';
import { useRequestFactChange } from '../hooks/useRequestFactChange.js';
import { useTruthFacts } from '../hooks/useTruthFacts.js';

import { BadgeCheck, SearchX } from 'lucide-react';

const FILTER_DEFAULTS = Object.freeze({
  searchFields: TRUTH_SEARCH_FIELDS,
  defaultSortField: TRUTH_SORT,
  defaultSortDirection: TRUTH_SORT_DIRECTION,
});

/**
 * Knowledge › Truth (CT-5719): what is confirmed about the record — Deal
 * commitments, Other facts and Canon — with where each fact came from, and
 * which are due to re-confirm.
 *
 * Search, the chips (All / To re-confirm / Confirmed by you), All filters
 * (Status and Source type, staged; no sort of its own, as in the prototype) and
 * the one order, "Needs attention first" with its direction, narrow on the
 * server. Truth is not edited here: Request change opens the change in
 * Helix Governance Studio. A fact opens in the profile slide-out
 * (`?item=truth:<factId>`).
 */
function TruthPage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, entity, itemsVersion = 0 } = useOutletContext();
  const [quickFilter, setQuickFilter] = useState('all');
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);

  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, sortField, sortDirection, hasActiveQuery, clearAll } = filtersState;

  const { sections, loading, error, accessDenied, refetch, refresh } = useTruthFacts({
    entityType,
    entityId,
    quickFilter,
    filters,
    search,
    orderBy: sortField,
    orderDirection: sortDirection,
  });

  // A report filed in the slide-out (outside this page) bumps `itemsVersion`.
  const lastItemsVersionRef = useRef(itemsVersion);
  useEffect(() => {
    if (lastItemsVersionRef.current === itemsVersion) return;
    lastItemsVersionRef.current = itemsVersion;
    refresh();
  }, [itemsVersion, refresh]);

  const { requestChange, requestingIds } = useRequestFactChange({ entityType, entityId });
  const { item, openItem } = useItemParam();
  const selectedId = item?.kind === ITEM_KINDS.TRUTH ? item.id : null;
  const handleOpen = useCallback((fact) => openItem(ITEM_KINDS.TRUTH, fact.id), [openItem]);

  const filterDefs = useMemo(
    () => [
      {
        id: 'status',
        label: t('knowledge.truth.filters.status'),
        options: TRUTH_STATUSES.map((value) => ({ value, label: t(`knowledge.truth.status.${value}`) })),
      },
      {
        id: 'source_kind',
        label: t('knowledge.truth.filters.sourceType'),
        options: KNOWLEDGE_SOURCE_TYPES.map((value) => ({ value, label: t(`knowledge.sourceTypes.${value}`) })),
      },
    ],
    [t],
  );
  const sortDefs = useMemo(() => [{ value: TRUTH_SORT, label: t('knowledge.truth.sort.needsAttention') }], [t]);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);
  const clearEverything = useCallback(() => {
    clearAll();
    setQuickFilter('all');
  }, [clearAll, setQuickFilter]);

  const hasFacts = sections.some((section) => section.facts.length > 0);

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={3} />;
    if (accessDenied) return <AccessDeniedState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    // Nothing confirmed yet: Truth as a whole says so, not only Canon.
    if (!hasFacts && !hasActiveQuery && quickFilter === 'all') {
      return (
        <EmptyState
          testId="truth-empty-state"
          icon={BadgeCheck}
          title={t('knowledge.truth.empty.title')}
          subtitle={t('knowledge.truth.empty.subtitle')}
        />
      );
    }
    if (!hasFacts) {
      return (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('knowledge.truth.noResults.title')}
          subtitle={t('knowledge.truth.noResults.subtitle')}
          action={
            <Button variant="secondary" size="sm" onClick={clearEverything}>
              {t('knowledge.truth.noResults.clear')}
            </Button>
          }
        />
      );
    }
    return (
      <div className="flex flex-col gap-6">
        {sections.map((section) => (
          <TruthSection
            key={section.key}
            section={section}
            entityTitle={entity?.title ?? null}
            selectedId={selectedId}
            requestingIds={requestingIds}
            onOpen={handleOpen}
            onRequestChange={requestChange}
          />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="truth-page" className="flex min-w-0 flex-1 flex-col gap-4 pb-6">
      <DataListFilterBar
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
        searchPlaceholder={t('knowledge.truth.searchPlaceholder')}
        onAllFiltersClick={openAllFilters}
      />
      <KnowledgeQuickFilters
        ids={TRUTH_QUICK_FILTERS}
        labelPrefix="knowledge.truth.quickFilters"
        value={quickFilter}
        onChange={setQuickFilter}
      />
      <div className="flex min-w-0 flex-1 flex-col">{results()}</div>
      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
        showSort={false}
      />
    </div>
  );
}

export default TruthPage;
